// src/components/alerts/AlertRuleForm.js
// --- UPDATE (Alert System AI) ------------------------------------------------
//   • "Create rule" from an AI suggestion is a NEW rule: isEditing now needs an id (it was true
//     for any initialData, so the title said "Edit" and the page tried to update rule undefined).
//   • Empty active-time fields are sent as null (an empty string fails MySQL TIME columns).
// --- END UPDATE --------------------------------------------------------------
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    Button, VStack, FormControl, FormLabel, Input, Select, Textarea, NumberInput,
    NumberInputField, Switch, Checkbox, useToast, SimpleGrid, Spinner, Text,
    Box, Heading, CheckboxGroup, Stack
} from '@chakra-ui/react';
import * as sensorService from '../../services/sensorService';
import * as userService from '../../services/userService';

// --- UPDATE: Added 'level' to metric types ---
const metricTypes = ['psi', 'temperature', 'flow_rate', 'voltage', 'vibration', 'level']; 
const conditionTypes = ['below', 'above', 'equals', 'outside_range'];
const severities = ['low', 'medium', 'high', 'critical'];
const workOrderPriorities = ['low', 'medium', 'high'];

function AlertRuleForm({ isOpen, onClose, onSubmit, initialData = null, isLoading }) {
    const [formData, setFormData] = useState({});
    const [activeDays, setActiveDays] = useState([]);
    const dayOptions = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const [sensors, setSensors] = useState([]);
    const [users, setUsers] = useState([]);
    const [isLoadingSensors, setIsLoadingSensors] = useState(false);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const toast = useToast();
    const isEditing = !!(initialData && initialData.id); // --- UPDATE (Alert System AI): was !!initialData

    const defaultRuleValues = {
        name: '',
        sensor_id: '',
        asset_id: '',
        metric_type: metricTypes[0],
        condition_type: conditionTypes[0],
        threshold_value: '',
        threshold_value_upper: '',
        duration_minutes: 0,
        severity: severities[1],
        notification_message: '',
        auto_create_work_order: true,
        work_order_title_template: 'Alert: [sensor_id] - [metric_type] [condition_type] threshold',
        work_order_description_template: 'Automated work order for alert on sensor [sensor_id]. Condition: [metric_type] is [condition_type] [threshold_value] for [duration_minutes] minutes. Current value: [current_value]. Asset: [asset_id].',
        work_order_priority: workOrderPriorities[1],
        assigned_user_id_default: '',
        is_enabled: true,
        active_start_time: '',
        active_end_time: '',
    };

    const fetchSensors = useCallback(async () => {
        setIsLoadingSensors(true);
        try {
            const response = await sensorService.fetchSensors();
            setSensors(response.data || response || []);
        } catch (error) {
            toast({ title: "Error fetching sensors", description: error.message, status: "error", duration: 3000 });
        } finally {
            setIsLoadingSensors(false);
        }
    }, [toast]);

    const fetchUsersForAssignment = useCallback(async () => {
        setIsLoadingUsers(true);
        try {
            const response = await userService.fetchUsers({ role: ['tech', 'staff'], limit: 500 });
            setUsers(response.data || []);
        } catch (error) {
            toast({ title: "Error fetching users for assignment", description: error.message, status: "error", duration: 3000 });
        } finally {
            setIsLoadingUsers(false);
        }
    }, [toast]);

    useEffect(() => {
        if (isOpen) {
            fetchSensors();
            fetchUsersForAssignment();
            if (initialData) {
                setFormData({
                    ...defaultRuleValues,
                    ...initialData,
                    threshold_value_upper: initialData.threshold_value_upper ?? '',
                    assigned_user_id_default: initialData.assigned_user_id_default || '',
                });
                setActiveDays(initialData.active_days ? initialData.active_days.split(',').map(day => day.trim()) : []);
            } else {
                setFormData(defaultRuleValues);
                setActiveDays([]);
            }
        }
    }, [isOpen, initialData, fetchSensors, fetchUsersForAssignment]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleNumberChange = (valueAsString, valueAsNumber, name) => {
        setFormData(prev => ({ ...prev, [name]: isNaN(valueAsNumber) ? '' : valueAsNumber }));
    };
    
    const handleSensorChange = (e) => {
        const selectedSensorId = e.target.value;
        const selectedSensor = sensors.find(s => s.device_id === selectedSensorId);
        setFormData(prev => ({
            ...prev,
            sensor_id: selectedSensorId,
            asset_id: selectedSensor?.asset_id || prev.asset_id || '',
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (formData.condition_type === 'outside_range' && (formData.threshold_value_upper === '' || formData.threshold_value_upper === null || parseFloat(formData.threshold_value_upper) <= parseFloat(formData.threshold_value))) {
            toast({ title: "Validation Error", description: "For 'outside_range', upper threshold must be provided and greater than lower threshold.", status: "error", duration: 4000 });
            return;
        }
        
        const dataToSubmit = {
            ...formData,
            threshold_value: parseFloat(formData.threshold_value),
            threshold_value_upper: formData.threshold_value_upper !== '' ? parseFloat(formData.threshold_value_upper) : null,
            duration_minutes: parseInt(formData.duration_minutes, 10),
            asset_id: formData.asset_id || null,
            assigned_user_id_default: formData.assigned_user_id_default || null,
            notification_message: formData.notification_message || null,
            active_days: activeDays.join(','),
            // --- UPDATE (Alert System AI): '' is not a valid TIME
            active_start_time: formData.active_start_time || null,
            active_end_time: formData.active_end_time || null,
            // --- END UPDATE
        };
        onSubmit(dataToSubmit);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent as="form" onSubmit={handleSubmit}>
                <ModalHeader>{isEditing ? 'Edit Alert Rule' : 'Create New Alert Rule'}</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    <VStack spacing={4} align="stretch">
                        <FormControl isRequired>
                            <FormLabel>Rule Name</FormLabel>
                            <Input name="name" value={formData.name || ''} onChange={handleChange} placeholder="e.g., Low Pressure Pump A" />
                        </FormControl>

                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <FormControl isRequired>
                                <FormLabel>Sensor</FormLabel>
                                {isLoadingSensors ? <Spinner size="sm"/> : (
                                <Select name="sensor_id" value={formData.sensor_id || ''} onChange={handleSensorChange} placeholder="Select Sensor">
                                    {sensors.map(sensor => (
                                        <option key={sensor.device_id} value={sensor.device_id}>
                                            {sensor.name || sensor.device_id} ({sensor.location_description || 'No location'})
                                        </option>
                                    ))}
                                </Select>
                                )}
                                {formData.sensor_id && formData.asset_id && <Text fontSize="xs" mt={1}>Associated Asset ID: {formData.asset_id}</Text>}
                            </FormControl>

                            <FormControl>
                                <FormLabel>Asset ID (Auto-filled from sensor if available)</FormLabel>
                                <Input name="asset_id" value={formData.asset_id || ''} onChange={handleChange} placeholder="e.g., ASSET-123 (or leave blank)" />
                            </FormControl>
                        </SimpleGrid>

                        <FormControl isRequired>
                            <FormLabel>Metric Type</FormLabel>
                            <Select name="metric_type" value={formData.metric_type || ''} onChange={handleChange}>
                                {metricTypes.map(type => <option key={type} value={type}>{type}</option>)}
                            </Select>
                        </FormControl>

                        <SimpleGrid columns={{ base: 1, md: formData.condition_type === 'outside_range' ? 3 : 2 }} spacing={4}>
                            <FormControl isRequired>
                                <FormLabel>Condition Type</FormLabel>
                                <Select name="condition_type" value={formData.condition_type || ''} onChange={handleChange}>
                                    {conditionTypes.map(type => <option key={type} value={type}>{type.replace('_', ' ')}</option>)}
                                </Select>
                            </FormControl>

                            {/* --- UPDATE: Handled mapping levels to numeric DB values --- */}
                            <FormControl isRequired>
                                <FormLabel>{formData.condition_type === 'outside_range' ? 'Lower Threshold' : 'Threshold Value'}</FormLabel>
                                {formData.metric_type === 'level' ? (
                                    <Select 
                                        name="threshold_value" 
                                        value={formData.threshold_value} 
                                        onChange={(e) => handleNumberChange(e.target.value, parseFloat(e.target.value), 'threshold_value')}
                                    >
                                        <option value="">Select Level</option>
                                        <option value="100">High (100)</option>
                                        <option value="60">Medium (60)</option>
                                        <option value="30">Low (30)</option>
                                        <option value="5">Empty (5)</option>
                                    </Select>
                                ) : (
                                    <NumberInput value={formData.threshold_value} onChange={(valStr, valNum) => handleNumberChange(valStr, valNum, 'threshold_value')}>
                                        <NumberInputField placeholder="e.g., 10.5" />
                                    </NumberInput>
                                )}
                            </FormControl>

                            {formData.condition_type === 'outside_range' && (
                                <FormControl isRequired>
                                    <FormLabel>Upper Threshold</FormLabel>
                                    {formData.metric_type === 'level' ? (
                                        <Select 
                                            name="threshold_value_upper" 
                                            value={formData.threshold_value_upper} 
                                            onChange={(e) => handleNumberChange(e.target.value, parseFloat(e.target.value), 'threshold_value_upper')}
                                        >
                                            <option value="">Select Level</option>
                                            <option value="100">High (100)</option>
                                            <option value="60">Medium (60)</option>
                                            <option value="30">Low (30)</option>
                                            <option value="5">Empty (5)</option>
                                        </Select>
                                    ) : (
                                        <NumberInput value={formData.threshold_value_upper} onChange={(valStr, valNum) => handleNumberChange(valStr, valNum, 'threshold_value_upper')}>
                                            <NumberInputField placeholder="e.g., 20.5" />
                                        </NumberInput>
                                    )}
                                </FormControl>
                            )}
                            {/* --- END UPDATE --- */}
                        </SimpleGrid>

                        <FormControl isRequired>
                            <FormLabel>Duration (minutes for condition to persist)</FormLabel>
                            <NumberInput min={0} name="duration_minutes" value={formData.duration_minutes || 0} onChange={(valStr, valNum) => handleNumberChange(valStr, valNum, 'duration_minutes')}>
                                <NumberInputField />
                            </NumberInput>
                            <Text fontSize="xs">Set to 0 for immediate alert on condition match.</Text>
                        </FormControl>

                        <Box w="100%" p={4} borderWidth="1px" borderRadius="lg">
                            <Heading size="sm" mb={4}>Optional: Active Time Window</Heading>
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                <FormControl>
                                    <FormLabel>Active From (Time)</FormLabel>
                                    <Input
                                        name="active_start_time"
                                        type="time"
                                        value={formData.active_start_time || ''}
                                        onChange={handleChange}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Active Until (Time)</FormLabel>
                                    <Input
                                        name="active_end_time"
                                        type="time"
                                        value={formData.active_end_time || ''}
                                        onChange={handleChange}
                                    />
                                </FormControl>
                            </SimpleGrid>
                            <FormControl mt={4}>
                                <FormLabel>Active on Days</FormLabel>
                                <CheckboxGroup colorScheme="teal" value={activeDays} onChange={setActiveDays}>
                                    <Stack direction={{ base: "column", sm: "row" }} wrap="wrap" spacing={[1, 4]}>
                                        {dayOptions.map(day => (
                                            <Checkbox key={day} value={day}>{day}</Checkbox>
                                        ))}
                                    </Stack>
                                </CheckboxGroup>
                            </FormControl>
                        </Box>

                        <FormControl isRequired>
                            <FormLabel>Severity</FormLabel>
                            <Select name="severity" value={formData.severity || ''} onChange={handleChange}>
                                {severities.map(level => <option key={level} value={level}>{level.charAt(0).toUpperCase() + level.slice(1)}</option>)}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Notification Message (Optional)</FormLabel>
                            <Textarea name="notification_message" value={formData.notification_message || ''} onChange={handleChange} placeholder="Custom message for this alert. Use placeholders like [sensor_id], [current_value]." />
                        </FormControl>

                        <FormControl display="flex" alignItems="center">
                            <FormLabel htmlFor="auto_create_work_order" mb="0">
                                Auto-create Work Order?
                            </FormLabel>
                            <Switch id="auto_create_work_order" name="auto_create_work_order" isChecked={formData.auto_create_work_order} onChange={handleChange} />
                        </FormControl>

                        {formData.auto_create_work_order && (
                            <VStack spacing={4} align="stretch" p={4} borderWidth="1px" borderRadius="md" mt={2}>
                                <FormControl>
                                    <FormLabel>Work Order Title Template</FormLabel>
                                    <Input name="work_order_title_template" value={formData.work_order_title_template || ''} onChange={handleChange} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Work Order Description Template</FormLabel>
                                    <Textarea name="work_order_description_template" value={formData.work_order_description_template || ''} onChange={handleChange} rows={3} />
                                        <Text fontSize="xs">Placeholders: [sensor_id], [sensor_name], [asset_id], [asset_location_description], [metric_type], [condition_type], [threshold_value], [threshold_value_upper], [current_value], [duration_minutes], [rule_name], [severity], [reading_timestamp]</Text>
                                </FormControl>
                                <SimpleGrid columns={2} spacing={4}>
                                <FormControl>
                                    <FormLabel>Work Order Priority</FormLabel>
                                    <Select name="work_order_priority" value={formData.work_order_priority || ''} onChange={handleChange}>
                                        {workOrderPriorities.map(prio => <option key={prio} value={prio}>{prio.charAt(0).toUpperCase() + prio.slice(1)}</option>)}
                                    </Select>
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Default Assigned User (Optional)</FormLabel>
                                    {isLoadingUsers ? <Spinner size="sm"/> : (
                                    <Select name="assigned_user_id_default" value={formData.assigned_user_id_default || ''} onChange={handleChange} placeholder="Select default user">
                                        {users.map(user => (
                                            <option key={user.id} value={user.id}>{user.username} ({user.role})</option>
                                        ))}
                                    </Select>
                                    )}
                                </FormControl>
                                </SimpleGrid>
                            </VStack>
                        )}

                        <FormControl display="flex" alignItems="center">
                            <FormLabel htmlFor="is_enabled" mb="0">
                                Rule Enabled?
                            </FormLabel>
                            <Switch id="is_enabled" name="is_enabled" isChecked={formData.is_enabled} onChange={handleChange} />
                        </FormControl>
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button type="submit" colorScheme="teal" mr={3} isLoading={isLoading}>
                        {isEditing ? 'Save Changes' : 'Create Rule'}
                    </Button>
                    <Button variant='ghost' onClick={onClose}>Cancel</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

AlertRuleForm.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    isLoading: PropTypes.bool,
};

export default AlertRuleForm;